/**
 * Generation & episode history (spec sections 29, 30, 54).
 *
 * One table covers both automated generations and manual Vids imports,
 * because from the operator's point of view they are the same question:
 * "which scene did I already make, and how?"
 */

import { storage } from './db.js';
import { STORAGE } from '../core/config.js';
import { uid } from '../utils/misc.js';
import { generationSignature, similarity } from '../utils/hash.js';

const STORE = STORAGE.stores.history;

export const HISTORY_STATUS = ['QUEUED', 'PROCESSING', 'COMPLETED', 'FAILED', 'CANCELLED'];
export const HISTORY_KINDS = ['automated-generation', 'manual-generation', 'episode-render', 'import'];

export function createHistoryEntry({
  kind = 'manual-generation',
  providerId = 'local-library',
  sceneId = null,
  sceneName = null,
  prompt = '',
  promptId = null,
  model = null,
  durationSeconds = null,
  aspectRatio = null,
  resolution = null,
  status = 'COMPLETED',
  assetId = null,
  error = null,
  notes = null,
  estimatedCostUsd = null,
}) {
  return {
    id: uid('hist'),
    kind,
    providerId,
    sceneId,
    sceneName,
    prompt,
    promptId,
    model,
    durationSeconds,
    aspectRatio,
    resolution,
    status,
    assetId,
    error,
    notes,
    estimatedCostUsd,
    signature: generationSignature({ providerId, sceneId, prompt, durationSeconds, aspectRatio, resolution, model }),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

export async function addHistoryEntry(entry) {
  await storage.put(STORE, entry);
  return entry;
}

export async function updateHistoryEntry(id, patch) {
  const existing = await storage.get(STORE, id);
  if (!existing) return null;
  const next = { ...existing, ...patch, updatedAt: new Date().toISOString() };
  await storage.put(STORE, next);
  return next;
}

export async function listHistory({ kind = null, sceneId = null, limit = 200 } = {}) {
  let all = await storage.getAll(STORE);
  if (kind) all = all.filter((e) => e.kind === kind);
  if (sceneId) all = all.filter((e) => e.sceneId === sceneId);
  return all
    .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))
    .slice(0, limit);
}

export async function deleteHistoryEntry(id) {
  return storage.delete(STORE, id);
}

export async function clearHistory() {
  return storage.clear(STORE);
}

/**
 * Duplicate prevention (spec section 54).
 *
 * Exact signature match is a definite duplicate. Anything above the
 * similarity threshold is reported as "nearly identical" and the UI
 * asks before spending money.
 */
export function findSimilarGenerations(entries, candidate, { similarityThreshold = 0.8 } = {}) {
  const exact = [];
  const near = [];
  for (const entry of entries) {
    if (entry.kind === 'episode-render') continue;
    if (entry.signature && entry.signature === candidate.signature) {
      exact.push(entry);
      continue;
    }
    if (candidate.prompt && entry.prompt) {
      const score = similarity(entry.prompt, candidate.prompt);
      if (score >= similarityThreshold) near.push({ entry, score: Math.round(score * 100) / 100 });
    }
  }
  return { exact, near: near.sort((a, b) => b.score - a.score).slice(0, 3) };
}

/** Which scenes already have at least one successful generation. */
export async function generatedSceneIds() {
  const all = await listHistory();
  return Array.from(
    new Set(all.filter((e) => e.status === 'COMPLETED' && e.sceneId).map((e) => e.sceneId)),
  );
}

/** Cost summary for the history view. */
export function summariseHistory(entries) {
  const completed = entries.filter((e) => e.status === 'COMPLETED');
  const failed = entries.filter((e) => e.status === 'FAILED');
  const cost = entries.reduce((sum, e) => sum + (Number(e.estimatedCostUsd) || 0), 0);
  return {
    total: entries.length,
    completed: completed.length,
    failed: failed.length,
    cancelled: entries.filter((e) => e.status === 'CANCELLED').length,
    estimatedCostUsd: Math.round(cost * 1000) / 1000,
  };
}
